import React, { useEffect, useState, useCallback } from 'react';
import { edificiosApi, novedadesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Bell, Plus, Loader2, Paperclip, X, FileText, Image as ImageIcon,
  Pencil, Trash2, Send, Archive, Undo2,
} from 'lucide-react';

const ESTADO_STYLE = {
  borrador: 'bg-slate-100 text-slate-600 border-slate-200',
  publicada: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archivada: 'bg-amber-50 text-amber-700 border-amber-200',
};
const ESTADO_LABEL = { borrador: 'Borrador', publicada: 'Publicada', archivada: 'Archivada' };

const fmtFecha = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const EstadoBadge = ({ estado }) => (
  <span data-testid={`novedad-estado-${estado}`} className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border ${ESTADO_STYLE[estado] || ESTADO_STYLE.borrador}`}>
    {ESTADO_LABEL[estado] || estado}
  </span>
);

const emptyForm = { novedad_id: null, edificio_id: '', titulo: '', texto: '', estado: 'borrador', adjuntos: [] };

export default function RevisorNovedades() {
  const [edificios, setEdificios] = useState([]);
  const [filtroEdificio, setFiltroEdificio] = useState('todos');
  const [novedades, setNovedades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtroEdificio !== 'todos' ? { edificio_id: filtroEdificio } : {};
      const r = await novedadesApi.list(params);
      setNovedades(r.data || []);
    } catch {
      toast.error('No se pudieron cargar las novedades');
    } finally { setLoading(false); }
  }, [filtroEdificio]);

  useEffect(() => {
    edificiosApi.list().then((r) => setEdificios(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = () => {
    setForm({ ...emptyForm, edificio_id: edificios[0]?.edificio_id || '' });
    setShowForm(true);
  };

  const openEdit = (n) => {
    setForm({
      novedad_id: n.novedad_id, edificio_id: n.edificio_id, titulo: n.titulo,
      texto: n.texto, estado: n.estado, adjuntos: n.adjuntos || [],
    });
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await novedadesApi.upload(fd);
      setForm((f) => ({ ...f, adjuntos: [...f.adjuntos, r.data] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir el archivo');
    } finally { setUploading(false); }
  };

  const removeAdjunto = (fileId) => {
    setForm((f) => ({ ...f, adjuntos: f.adjuntos.filter((a) => a.file_id !== fileId) }));
  };

  const handleSave = async () => {
    if (!form.edificio_id) return toast.error('Elegí un edificio');
    if (!form.titulo.trim()) return toast.error('El título es obligatorio');
    if (!form.texto.trim()) return toast.error('El texto es obligatorio');
    setSaving(true);
    try {
      const payload = {
        edificio_id: form.edificio_id, titulo: form.titulo, texto: form.texto,
        estado: form.estado, adjuntos: form.adjuntos.map((a) => a.file_id),
      };
      if (form.novedad_id) {
        await novedadesApi.actualizar(form.novedad_id, payload);
        toast.success('Novedad actualizada');
      } else {
        await novedadesApi.crear(payload);
        toast.success('Novedad creada');
      }
      setShowForm(false);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  const cambiarEstado = async (n, estado) => {
    try {
      await novedadesApi.cambiarEstado(n.novedad_id, estado);
      toast.success(`Novedad ${ESTADO_LABEL[estado].toLowerCase()}`);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar el estado');
    }
  };

  const handleDelete = async () => {
    try {
      await novedadesApi.eliminar(deleteId);
      toast.success('Novedad eliminada');
      setDeleteId(null);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-indigo-700" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Novedades</h1>
            <p className="text-slate-500 mt-1">Actualizaciones por edificio para inversores</p>
          </div>
        </div>
        <Button onClick={openCreate} data-testid="nueva-novedad-btn" className="bg-[#1B4D3E] hover:bg-[#163f33]">
          <Plus className="w-4 h-4 mr-1" /> Nueva novedad
        </Button>
      </div>

      <div className="mb-4 w-full sm:w-64">
        <Select value={filtroEdificio} onValueChange={setFiltroEdificio}>
          <SelectTrigger data-testid="filtro-edificio-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los edificios</SelectItem>
            {edificios.map((e) => (
              <SelectItem key={e.edificio_id} value={e.edificio_id}>{e.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : novedades.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white border border-slate-200 rounded-lg">
          No hay novedades. Creá la primera con "Nueva novedad".
        </div>
      ) : (
        <div className="space-y-3" data-testid="novedades-list">
          {novedades.map((n) => (
            <div key={n.novedad_id} data-testid={`novedad-card-${n.novedad_id}`} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900">{n.titulo}</h3>
                    <EstadoBadge estado={n.estado} />
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{n.edificio_nombre} · {fmtFecha(n.created_at)}</p>
                  <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap line-clamp-3">{n.texto}</p>
                  {(n.adjuntos || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {n.adjuntos.map((a) => (
                        <a key={a.file_id} href={novedadesApi.downloadUrl(a.file_id)} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-1 hover:bg-indigo-100">
                          {a.content_type?.includes('pdf') ? <FileText className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                          <span className="max-w-[140px] truncate">{a.original_filename}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(n)} data-testid={`editar-${n.novedad_id}`} title="Editar">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setDeleteId(n.novedad_id)} data-testid={`eliminar-${n.novedad_id}`} title="Eliminar">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                {n.estado !== 'publicada' && (
                  <Button size="sm" variant="outline" onClick={() => cambiarEstado(n, 'publicada')} data-testid={`publicar-${n.novedad_id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Publicar
                  </Button>
                )}
                {n.estado === 'publicada' && (
                  <Button size="sm" variant="outline" onClick={() => cambiarEstado(n, 'archivada')} data-testid={`archivar-${n.novedad_id}`}>
                    <Archive className="w-3.5 h-3.5 mr-1" /> Archivar
                  </Button>
                )}
                {n.estado === 'archivada' && (
                  <Button size="sm" variant="outline" onClick={() => cambiarEstado(n, 'borrador')} data-testid={`reactivar-${n.novedad_id}`}>
                    <Undo2 className="w-3.5 h-3.5 mr-1" /> Pasar a borrador
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Crear / Editar */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{form.novedad_id ? 'Editar novedad' : 'Nueva novedad'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Edificio</label>
              <Select value={form.edificio_id} onValueChange={(v) => setForm((f) => ({ ...f, edificio_id: v }))}>
                <SelectTrigger data-testid="form-edificio-select" className="mt-1"><SelectValue placeholder="Elegí un edificio" /></SelectTrigger>
                <SelectContent>
                  {edificios.map((e) => (
                    <SelectItem key={e.edificio_id} value={e.edificio_id}>{e.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Título</label>
              <Input value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
                data-testid="form-titulo-input" className="mt-1" placeholder="Ej: Avance de obra - Junio" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Texto</label>
              <Textarea value={form.texto} onChange={(e) => setForm((f) => ({ ...f, texto: e.target.value }))}
                data-testid="form-texto-input" className="mt-1" rows={5} placeholder="Detalle de la novedad…" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Estado</label>
              <Select value={form.estado} onValueChange={(v) => setForm((f) => ({ ...f, estado: v }))}>
                <SelectTrigger data-testid="form-estado-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="borrador">Borrador</SelectItem>
                  <SelectItem value="publicada">Publicada</SelectItem>
                  <SelectItem value="archivada">Archivada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Adjuntos (imágenes / PDF)</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {form.adjuntos.map((a) => (
                  <span key={a.file_id} className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1">
                    {a.content_type?.includes('pdf') ? <FileText className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                    <span className="max-w-[120px] truncate">{a.original_filename}</span>
                    <button onClick={() => removeAdjunto(a.file_id)} className="text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                  </span>
                ))}
                <label className="inline-flex items-center gap-1 text-xs text-indigo-700 border border-dashed border-indigo-300 rounded px-2 py-1 cursor-pointer hover:bg-indigo-50">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />} Adjuntar
                  <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.pdf" className="hidden" onChange={handleUpload} data-testid="form-adjunto-input" />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="guardar-novedad-btn" className="bg-[#1B4D3E] hover:bg-[#163f33]">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar novedad?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-eliminar-btn">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
